import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  BookOpen, Check, ChevronLeft, ChevronRight, Eye, FileUp, Globe, Layers,
  Lightbulb, Loader2, Lock, Map, Mic, MicOff, PenTool, Plus, RefreshCw, Save,
  Sparkles, Square, Star, Stethoscope, Target, Upload, Users, Wand2, Zap
} from "lucide-react";
import { getDraftKey } from "../lib/draftStorage";
import StoryDnaPanel from "./StoryDnaPanel";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { playTick, playComplete } from "../sounds";
import ShweebumCat from "./ShweebumCat";
import UploadFlowSteps from "./UploadFlowSteps";
import SpeechToTextButton from "./SpeechToTextButton";
import ActivityBox, { useActivityLogs } from "./ActivityBox";
import CharacterEditor from "./CharacterEditor";
import StoryBuilderWizard from "./StoryBuilderWizard";
import { useAuth } from "./Auth/AuthProvider";
import { API, pollManuscriptOutlines } from "../lib/api";
import ModeSelector from "./new-project/ModeSelector";
import UploadMode from "./new-project/UploadMode";
import ManualBuilderMode from "./new-project/ManualBuilderMode";
import JourneyMode from "./new-project/JourneyMode";
import IdeaMode from "./new-project/IdeaMode";
import QuickStoryMode from "./new-project/QuickStoryMode";
import VoiceMode from "./new-project/VoiceMode";
import GenreTemplateMode from "./new-project/GenreTemplateMode";

const NewProjectDialog = ({ open, onOpenChange, genres, userId, onCreated, freebumMode = false }) => {
  const navigate = useNavigate();
  const [mode, setMode] = useState("choose"); // "choose", "builder", "manual", "idea", "journey", "quick", "voice", "upload"
  const [step, setStep] = useState(1);
  const [bookOptions, setBookOptions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generatingOutline, setGeneratingOutline] = useState(false);
  const [outlineProgress, setOutlineProgress] = useState({ part: 0, total: 4, message: '' });
  const [outline, setOutline] = useState(null);
  
  // Complexity level for Manual mode: "simple", "moderate", "detailed"
  const [complexityLevel, setComplexityLevel] = useState(null);
  
  // Chapter Planning state (new step before book creation)
  const [chapterPlanMode, setChapterPlanMode] = useState(null); // null, 'manual', 'situations', 'ai', 'upload'
  const [manualChapters, setManualChapters] = useState([]);
  const [userSituations, setUserSituations] = useState([{ description: '' }]);
  const [planReviewOutline, setPlanReviewOutline] = useState(null);
  const [generatingPlan, setGeneratingPlan] = useState(false);
  const [uploadingOutline, setUploadingOutline] = useState(false); // For chapter outline upload
  
  // Smart Story Bible Upload state
  const [smartUploadMode, setSmartUploadMode] = useState(false);
  const [uploadingStoryBible, setUploadingStoryBible] = useState(false);
  const [uploadedStoryBible, setUploadedStoryBible] = useState(null);
  const [uploadConflicts, setUploadConflicts] = useState([]); // Detected conflicts
  const [conflictResolutions, setConflictResolutions] = useState({}); // User's choices
  const [resolvingConflicts, setResolvingConflicts] = useState(false);
  
  // Category-based upload state
  const [uploadCategory, setUploadCategory] = useState("auto"); // "auto", "characters", "chapters", "world", "synopsis"
  const [uploadedCategories, setUploadedCategories] = useState({}); // Track what's been uploaded by category
  // Example: { characters: { status: 'success', count: 5, data: [...] }, chapters: { status: 'success', count: 54, data: [...] } }
  const [activeUploadCard, setActiveUploadCard] = useState(null); // Which card is expanded to show details/conflicts
  const [uploadProgress, setUploadProgress] = useState(0); // 0-100 for progress bar
  const [uploadProgressMessage, setUploadProgressMessage] = useState(''); // Current step message
  
  // NEW: Multi-step upload flow state
  const [uploadFlowStep, setUploadFlowStep] = useState(1); // 1: chapters, 2: characters, 3: world rules
  const [uploadedChaptersVerbatim, setUploadedChaptersVerbatim] = useState([]); // Verbatim chapter content
  const [uploadedCharacterProfiles, setUploadedCharacterProfiles] = useState([]); // Character data
  const [uploadedWorldRules, setUploadedWorldRules] = useState(null); // World building data
  
  // Manuscript Upload state
  const [manuscriptId, setManuscriptId] = useState(null);
  const [manuscriptAnalysis, setManuscriptAnalysis] = useState(null);
  const [manuscriptOutlines, setManuscriptOutlines] = useState(null);
  const [manuscriptDiagnosis, setManuscriptDiagnosis] = useState(null);
  const [uploadStep, setUploadStep] = useState("upload"); // "upload", "analyzing", "results", "outlines", "diagnose", "create"
  const [uploadingFile, setUploadingFile] = useState(false);
  const [analyzingFile, setAnalyzingFile] = useState(false);
  const [selectedImprovements, setSelectedImprovements] = useState([]);
  const [diagnosisMode, setDiagnosisMode] = useState("select"); // "select" or "discuss"
  const [uploadActivityLog, setUploadActivityLog] = useState([]);
  const [applyingIdea, setApplyingIdea] = useState(null);
  const [appliedIdeas, setAppliedIdeas] = useState([]);
  const [previewChanges, setPreviewChanges] = useState(null);
  const [previewingIdea, setPreviewingIdea] = useState(null);
  
  // Fix Issue state
  const [fixingIssue, setFixingIssue] = useState(null); // { type, index, description }
  const [fixedIssues, setFixedIssues] = useState([]); // Track which issues have been fixed
  
  // Chapter Size Options - applies to all book generation modes
  const [chapterSize, setChapterSize] = useState("auto"); // "auto" (outline-driven), "medium" (800-1000 words) or "large" (1800-2100 words)
  
  // Manual creation form - expanded for world-building
  const [formData, setFormData] = useState({
    title: "",
    genre: "",
    book_type: "fiction",
    target_length: "novel",
    audience_age: "adult",
    content_rating: "moderate",
    specialty_subject: "general",
    tone: "balanced",
    protagonists: [{ name: "", description: "" }],  // Support multiple protagonists
    setting: "",
    initial_premise: "",
    // Series info
    is_series: false,
    series_name: "",
    total_books: 1,
    book_number: 1,
    // Author credentials for non-fiction types
    author_credentials: "",
    // NEW: World-Building Fields
    num_chapters: 10,
    pov: "third_limited", // first, third_limited, third_omniscient
    tense: "past", // past, present, future
    // World settings
    world_type: "earth", // earth, alternate_earth, fantasy, scifi, other
    world_name: "",
    era: "contemporary", // ancient, medieval, renaissance, victorian, modern, future, custom
    era_custom: "",
    // Technology
    tech_level: "modern", // none, primitive, medieval, industrial, modern, advanced, scifi
    tech_importance: "background", // none, background, moderate, central
    // Supernatural/Magic
    has_supernatural: false,
    supernatural_type: [], // magic, demons, angels, psychic, spirits, gods, other
    supernatural_rules: "",
    supernatural_source: "",
    // Religion
    has_religion: false,
    religion_type: "mono", // mono, poly, spiritual, none, custom
    religion_importance: "background", // none, background, moderate, central
    religion_description: "",
    // Society
    society_structure: "democratic", // tribal, feudal, monarchy, democratic, authoritarian, anarchic, other
    social_classes: false,
    // Characters (expanded)
    antagonist_name: "",
    antagonist_description: "",
    antagonist_type: "human", // human, creature, supernatural, nature, society, self, none
    // Story arc
    story_arc: "hero_journey", // hero_journey, rags_to_riches, tragedy, rebirth, quest, voyage_return, comedy, overcoming_monster
    central_conflict: "",
    theme: "",
    // Story Premise (Beginning/Middle/End)
    premise_beginning: "",  // What the world looks like before the main conflict
    premise_middle: "",     // The rising conflict and major turning points
    premise_ending: "",     // How the story resolves and characters change
    // Elevator pitch
    elevator_pitch: ""
  });
  
  // Idea wizard form
  const [ideaData, setIdeaData] = useState({
    title: "",
    idea: "",
    genre: "",
    num_chapters: 10,
    audience_age: "adult",
    content_rating: "moderate",
    tone: "balanced",
    location: "",
    time_period: "",
    must_have_points: ""
  });

  // Quick Story form data
  const [quickStoryData, setQuickStoryData] = useState({
    idea: "",
    title: "",
    genre: "literary",
    word_count: 1500,
    audience_age: "adult",
    content_rating: "moderate",
    tone: "balanced"
  });
  const [generatingQuickStory, setGeneratingQuickStory] = useState(false);
  const [quickStoryProgress, setQuickStoryProgress] = useState({ step: 0, total: 4, message: '' });
  // Story DNA Panel state (P0 #1 + #2)
  const [quickStoryCanon, setQuickStoryCanon] = useState(null);  // canon dict from /canon/extract
  const [quickStoryLocks, setQuickStoryLocks] = useState(new Set()); // 🔒 user-selected lock strings
  const [quickStoryDnaConfirmed, setQuickStoryDnaConfirmed] = useState(false);

  // Voice to Story state
  const [voiceTranscript, setVoiceTranscript] = useState("");
  const [isRecordingVoice, setIsRecordingVoice] = useState(false);
  const [voiceGenre, setVoiceGenre] = useState("literary");
  const [voiceTargetLength, setVoiceTargetLength] = useState("short");
  const [processingVoice, setProcessingVoice] = useState(false);
  const voiceRecognitionRef = useRef(null);

  // Journey/Blueprint wizard data
  const [blueprintData, setBlueprintData] = useState({
    title: "",
    protagonists: [{ name: "", description: "" }],  // Support multiple protagonists
    antagonist_name: "",
    antagonist_description: "",
    genre: "",
    tone: "",
    num_chapters: 10,
    world_rules: "",
  });
  const [situations, setSituations] = useState([
    { description: "", time_marker: "Beginning of the story" }
  ]);
  const [blueprintOutline, setBlueprintOutline] = useState(null);
  const [generatingBlueprint, setGeneratingBlueprint] = useState(false);
  const [blueprintProgress, setBlueprintProgress] = useState({ part: 0, total: 3, message: '' });
  const [showDraftPicker, setShowDraftPicker] = useState(false);
  const [availableDrafts, setAvailableDrafts] = useState([]);

  // Activity logging for all modes
  const { logs: activityLogs, addLog, clearLogs } = useActivityLogs();
  
  // Characters state for smart upload
  const [characters, setCharacters] = useState([]);
  
  // Track if there are unsaved changes
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastAutoSave, setLastAutoSave] = useState(null);

  // Auto-save drafts every 30 seconds when there's data
  useEffect(() => {
    if (!open || mode === "choose") return;
    
    const autoSave = () => {
      try {
        let draftData = null;
        let draftKey = '';
        
        if (mode === "journey" && blueprintData.title) {
          draftKey = `journey_${blueprintData.title.toLowerCase().trim()}`;
          draftData = {
            mode: "journey",
            blueprintData,
            situations,
            savedAt: new Date().toISOString(),
            title: blueprintData.title,
            isAutoSave: true
          };
        } else if (mode === "manual" && formData.title) {
          draftKey = `manual_${formData.title.toLowerCase().trim()}`;
          draftData = {
            mode: "manual",
            formData,
            savedAt: new Date().toISOString(),
            title: formData.title,
            isAutoSave: true
          };
        } else if (mode === "idea" && ideaData.title) {
          draftKey = `idea_${ideaData.title.toLowerCase().trim()}`;
          draftData = {
            mode: "idea",
            ideaData,
            savedAt: new Date().toISOString(),
            title: ideaData.title,
            isAutoSave: true
          };
        }
        
        if (draftData && draftKey) {
          const drafts = JSON.parse(localStorage.getItem(getDraftKey('story')) || '{}');
          drafts[draftKey] = draftData;
          localStorage.setItem(getDraftKey('story'), JSON.stringify(drafts));
          setLastAutoSave(new Date());
          setHasUnsavedChanges(false);
        }
      } catch (err) {
        console.error('Auto-save failed:', err);
      }
    };
    
    const interval = setInterval(autoSave, 30000); // Auto-save every 30 seconds
    return () => clearInterval(interval);
  }, [open, mode, blueprintData, situations, formData, ideaData]);
  
  // Mark as having unsaved changes when data changes
  useEffect(() => {
    if (mode !== "choose" && (blueprintData.title || formData.title || ideaData.title)) {
      setHasUnsavedChanges(true);
    }
  }, [blueprintData, formData, ideaData, situations, mode]);
  
  // Warn before closing with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges && open) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return e.returnValue;
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges, open]);
  
  // Listen for draft load events from the main page
  useEffect(() => {
    const handleLoadDraft = (e) => {
      const draft = e.detail;
      if (!draft) return;
      
      if (draft.mode === 'journey' && draft.blueprintData) {
        setBlueprintData(draft.blueprintData);
        if (draft.situations) setSituations(draft.situations);
        setMode("journey");
        setStep(2);
        toast.success(`Loaded draft: "${draft.title}"`);
      } else if (draft.mode === 'manual' && draft.formData) {
        setFormData(draft.formData);
        setMode("manual");
        toast.success(`Loaded draft: "${draft.title}"`);
      } else if (draft.mode === 'idea' && draft.ideaData) {
        setIdeaData(draft.ideaData);
        setMode("idea");
        toast.success(`Loaded draft: "${draft.title}"`);
      }
      setHasUnsavedChanges(false);
    };
    
    window.addEventListener('load-draft', handleLoadDraft);
    return () => window.removeEventListener('load-draft', handleLoadDraft);
  }, []);

  // Fetch book options on mount
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const res = await axios.get(`${API}/book-options`);
        setBookOptions(res.data);
      } catch (error) {
        console.error("Failed to fetch book options");
      }
    };
    if (open) fetchOptions();
  }, [open]);

  // Auto-fill characters from uploaded data when entering Step 2
  useEffect(() => {
    if (step === 2 && mode === "manual") {
      // Check if we have uploaded characters from the upload flow
      const uploadedChars = uploadedStoryBible?.characters || uploadedCharacterProfiles || [];
      
      // Only auto-fill if current protagonists are empty/default AND we have uploaded characters
      const currentProtags = formData.protagonists || [];
      const isEmptyOrDefault = currentProtags.length === 0 || 
        (currentProtags.length === 1 && !currentProtags[0].name && !currentProtags[0].description);
      
      if (uploadedChars.length > 0 && isEmptyOrDefault) {
        console.log(`Auto-filling ${uploadedChars.length} characters from uploaded data`);
        
        // Map uploaded characters to the protagonist format
        const mappedChars = uploadedChars.map((c, i) => ({
          name: c.name || '',
          role: c.role || 'supporting',
          description: c.description || c.brief_description || '',
          age: c.age || extractAge(c.physical_appearance) || '',
          gender: c.gender || extractGender(c.physical_appearance) || '',
          height: c.height || extractField(c.physical_appearance, 'height') || '',
          build: c.build || extractField(c.physical_appearance, 'build') || '',
          hairColor: c.hairColor || extractField(c.physical_appearance, 'hair') || '',
          hairStyle: c.hairStyle || '',
          eyeColor: c.eyeColor || extractField(c.physical_appearance, 'eyes') || '',
          skinTone: c.skinTone || '',
          distinguishingFeatures: c.distinguishingFeatures || extractField(c.physical_appearance, 'distinguishing') || '',
          personality: c.personality || c.personality_traits || '',
          motivation: c.motivation || c.motivation_and_goal || '',
          backstory: c.backstory || '',
        }));
        
        setFormData(prev => ({ ...prev, protagonists: mappedChars }));
        console.log(`Auto-filled ${mappedChars.length} characters into form`);
      }
    }
  }, [step, mode, uploadedStoryBible, uploadedCharacterProfiles]);

  // Auto-fill World-Building from uploaded data when entering Step 5 (detailed mode)
  useEffect(() => {
    if (step === 5 && mode === "manual" && complexityLevel === 'detailed') {
      const uploadedWorld = uploadedStoryBible?.world_building || uploadedWorldRules || {};
      
      // Only auto-fill if we have uploaded world data
      if (Object.keys(uploadedWorld).length > 0) {
        console.log('Auto-filling world-building from uploaded data');
        
        setFormData(prev => ({
          ...prev,
          world_type: uploadedWorld.world_type || prev.world_type,
          era: uploadedWorld.era || prev.era,
          world_name: uploadedWorld.world_name || prev.world_name,
          tech_level: uploadedWorld.tech_level || prev.tech_level,
          tech_importance: uploadedWorld.tech_importance || prev.tech_importance,
          society_structure: uploadedWorld.society_structure || prev.society_structure,
          // Also handle supernatural/magic from world rules
          has_supernatural: uploadedWorld.has_supernatural ?? prev.has_supernatural,
          magic_system: uploadedWorld.magic_system || prev.magic_system,
          supernatural_creatures: uploadedWorld.supernatural_creatures || prev.supernatural_creatures,
          religion_importance: uploadedWorld.religion_importance || prev.religion_importance,
          religion_type: uploadedWorld.religion_type || prev.religion_type,
        }));
      }
    }
  }, [step, mode, complexityLevel, uploadedStoryBible, uploadedWorldRules]);

  // Auto-fill Story Premise from uploaded data when entering Step 3 (moderate) or Step 4 (detailed)
  useEffect(() => {
    const isPremiseStep = (complexityLevel === 'moderate' && step === 3) || 
                          (complexityLevel === 'detailed' && step === 4);
    
    if (isPremiseStep && mode === "manual") {
      const storyStructure = uploadedStoryBible?.story_structure || {};
      const updates = {};
      
      // Only auto-fill if current fields are empty AND we have uploaded data
      if (storyStructure.beginning && !formData.premise_beginning) {
        updates.premise_beginning = storyStructure.beginning;
      }
      if (storyStructure.middle && !formData.premise_middle) {
        updates.premise_middle = storyStructure.middle;
      }
      if (storyStructure.ending && !formData.premise_ending) {
        updates.premise_ending = storyStructure.ending;
      }
      
      if (Object.keys(updates).length > 0) {
        console.log('Auto-filling story premise from uploaded data:', updates);
        setFormData(prev => ({ ...prev, ...updates }));
      }
    }
  }, [step, mode, complexityLevel, uploadedStoryBible]);

  // Auto-fill Story Details (audience, tone, antagonist) when entering Step 4 (moderate) or Step 5 (detailed)
  useEffect(() => {
    const isDetailsStep = (complexityLevel === 'moderate' && step === 4) || 
                          (complexityLevel === 'detailed' && step === 5);
    
    if (isDetailsStep && mode === "manual") {
      const uploadedChars = uploadedStoryBible?.characters || uploadedCharacterProfiles || [];
      const uploadedWorld = uploadedStoryBible?.world_building || uploadedWorldRules || {};
      const updates = {};
      
      // Auto-fill audience/tone from uploaded data
      if (uploadedStoryBible?.audience_age && !formData.audience_age) {
        updates.audience_age = uploadedStoryBible.audience_age;
      }
      if ((uploadedStoryBible?.tone || uploadedWorld?.tone) && !formData.tone) {
        updates.tone = uploadedStoryBible?.tone || uploadedWorld?.tone;
      }
      if (uploadedStoryBible?.content_rating && !formData.content_rating) {
        updates.content_rating = uploadedStoryBible.content_rating;
      }
      
      // Auto-fill antagonist from characters
      if (!formData.antagonist_name) {
        const antagonist = uploadedChars.find(c => 
          c.role === 'antagonist' || 
          c.role?.toLowerCase()?.includes('villain') ||
          c.role?.toLowerCase()?.includes('antagonist') ||
          c.role?.toLowerCase()?.includes('enemy')
        );
        if (antagonist) {
          updates.antagonist_name = antagonist.name || '';
          updates.antagonist_description = antagonist.description || antagonist.brief_description || '';
          console.log('Auto-filled antagonist from uploaded characters:', antagonist.name);
        }
      }
      
      if (Object.keys(updates).length > 0) {
        console.log('Auto-filling story details from uploaded data:', updates);
        setFormData(prev => ({ ...prev, ...updates }));
      }
    }
  }, [step, mode, complexityLevel, uploadedStoryBible, uploadedCharacterProfiles, uploadedWorldRules]);

  // Auto-set chapter plan mode and chapters when entering final step with uploaded chapters
  useEffect(() => {
    const isFinalStep = (complexityLevel === 'simple' && step === 3) || 
                        (complexityLevel === 'moderate' && step === 5) || 
                        (complexityLevel === 'detailed' && step === 7);
    
    if (isFinalStep && mode === "manual") {
      const uploadedChaps = uploadedStoryBible?.chapter_outlines || uploadedChaptersVerbatim || [];
      
      // If we have uploaded chapters and no plan mode selected yet, auto-select upload mode
      if (uploadedChaps.length > 0 && !chapterPlanMode) {
        console.log(`Auto-setting chapter plan mode to 'upload' with ${uploadedChaps.length} chapters`);
        
        // Update num_chapters to match uploaded chapters
        setFormData(prev => ({ 
          ...prev, 
          num_chapters: uploadedChaps.length 
        }));
        
        // Set the plan to review with uploaded chapters (verbatim)
        const chaptersForReview = uploadedChaps.map((ch, i) => ({
          number: ch.number || i + 1,
          title: ch.title || `Chapter ${ch.number || i + 1}`,
          summary: ch.summary || ch.content || '',
          key_beats: ch.key_beats || [],
          pov: ch.pov || '',
          setting: ch.setting || ''
        }));
        
        setPlanReviewOutline(chaptersForReview);
        setChapterPlanMode('upload');
        
        console.log(`Auto-loaded ${chaptersForReview.length} chapters for review`);
      }
    }
  }, [step, mode, complexityLevel, chapterPlanMode, uploadedStoryBible, uploadedChaptersVerbatim]);

  // Helper functions to extract fields from physical_appearance text
  const extractAge = (text) => {
    if (!text) return '';
    const match = text.match(/Age:\s*(\d+)/i);
    return match ? match[1] : '';
  };
  
  const extractGender = (text) => {
    if (!text) return '';
    const match = text.match(/Gender:\s*(\w+)/i);
    return match ? match[1].toLowerCase() : '';
  };
  
  const extractField = (text, field) => {
    if (!text) return '';
    const patterns = {
      height: /Height:\s*([^-,]+)/i,
      build: /Build:\s*([^-,]+)/i,
      hair: /Hair:\s*([^-,]+)/i,
      eyes: /Eyes:\s*([^-,]+)/i,
      distinguishing: /Distinguishing[^:]*:\s*([^-]+?)(?=\s*(?:Personality|Motivation|Backstory|$))/i,
    };
    const pattern = patterns[field];
    if (!pattern) return '';
    const match = text.match(pattern);
    return match ? match[1].trim() : '';
  };

  // Populate form from uploaded data (chapters, characters, world)
  const populateFormFromUploads = () => {
    const uploadedChaps = uploadedStoryBible?.chapter_outlines || uploadedChaptersVerbatim || [];
    const uploadedChars = uploadedStoryBible?.characters || uploadedCharacterProfiles || [];
    const uploadedWorld = uploadedStoryBible?.world_building || uploadedWorldRules || {};
    
    console.log(`Populating form from uploads: ${uploadedChaps.length} chapters, ${uploadedChars.length} characters`);
    
    // Build updates object
    const updates = {};
    
    // Title from uploadedStoryBible if available
    if (uploadedStoryBible?.title) {
      updates.title = uploadedStoryBible.title;
    }
    
    // Genre/theme from uploadedStoryBible
    if (uploadedStoryBible?.genre) {
      updates.genre = uploadedStoryBible.genre;
    }
    
    // Number of chapters from uploaded chapters
    if (uploadedChaps.length > 0) {
      updates.num_chapters = uploadedChaps.length;
    }
    
    // Characters - map uploaded characters to both protagonists and characters
    if (uploadedChars.length > 0) {
      const mappedChars = uploadedChars.map((c, i) => ({
        name: c.name || '',
        role: c.role || 'supporting',
        description: c.description || c.brief_description || '',
        physical_appearance: c.physical_appearance || '',
        extraction_method: c.extraction_method || '',
        age: c.age || extractAge(c.physical_appearance) || '',
        gender: c.gender || extractGender(c.physical_appearance) || '',
        height: c.height || extractField(c.physical_appearance, 'height') || '',
        build: c.build || extractField(c.physical_appearance, 'build') || '',
        hairColor: c.hairColor || c.hair_color || extractField(c.physical_appearance, 'hair') || '',
        hairStyle: c.hairStyle || c.hair_style || '',
        eyeColor: c.eyeColor || c.eye_color || extractField(c.physical_appearance, 'eyes') || '',
        skinTone: c.skinTone || c.skin_tone || '',
        distinguishingFeatures: c.distinguishingFeatures || c.distinguishing_features || extractField(c.physical_appearance, 'distinguishing') || '',
        personality: c.personality || c.personality_traits || '',
        motivation: c.motivation || c.motivation_and_goal || '',
        backstory: c.backstory || '',
      }));
      updates.protagonists = mappedChars;
      // Also update the characters state directly
      if (setCharacters) {
        setCharacters(mappedChars);
      }
    }
    
    // World-building fields
    if (Object.keys(uploadedWorld).length > 0) {
      updates.world_type = uploadedWorld.world_type || updates.world_type;
      updates.era = uploadedWorld.era || updates.era;
      updates.world_name = uploadedWorld.world_name || updates.world_name;
      updates.tech_level = uploadedWorld.tech_level || updates.tech_level;
      updates.tech_importance = uploadedWorld.tech_importance || updates.tech_importance;
      updates.society_structure = uploadedWorld.society_structure || updates.society_structure;
      updates.has_supernatural = uploadedWorld.has_supernatural ?? updates.has_supernatural;
      updates.magic_system = uploadedWorld.magic_system || updates.magic_system;
    }
    
    // Preserve existing premise fields (set by onComplete from story structure)
    // Only set if not already in formData
    if (uploadedStoryBible?.elevator_pitch && !formData.elevator_pitch) {
      updates.elevator_pitch = uploadedStoryBible.elevator_pitch;
    }
    
    // Auto-fill Story Structure (premise_beginning/middle/ending) from uploaded data
    const storyStructure = uploadedStoryBible?.story_structure || {};
    if (storyStructure.beginning && !formData.premise_beginning) {
      updates.premise_beginning = storyStructure.beginning;
    }
    if (storyStructure.middle && !formData.premise_middle) {
      updates.premise_middle = storyStructure.middle;
    }
    if (storyStructure.ending && !formData.premise_ending) {
      updates.premise_ending = storyStructure.ending;
    }
    
    // Auto-fill Story Details (audience, tone, content_rating)
    if (uploadedStoryBible?.audience_age && !formData.audience_age) {
      updates.audience_age = uploadedStoryBible.audience_age;
    }
    if (uploadedStoryBible?.tone && !formData.tone) {
      updates.tone = uploadedStoryBible.tone;
    }
    if (uploadedStoryBible?.content_rating && !formData.content_rating) {
      updates.content_rating = uploadedStoryBible.content_rating;
    }
    if (uploadedWorld?.tone && !formData.tone) {
      updates.tone = uploadedWorld.tone;
    }
    
    // Auto-fill Antagonist from characters with antagonist role
    if (!formData.antagonist_name) {
      const antagonist = uploadedChars.find(c => 
        c.role === 'antagonist' || 
        c.role?.toLowerCase().includes('villain') ||
        c.role?.toLowerCase().includes('antagonist')
      );
      if (antagonist) {
        updates.antagonist_name = antagonist.name || '';
        updates.antagonist_description = antagonist.description || antagonist.brief_description || '';
        updates.antagonist_type = antagonist.antagonist_type || 'human';
      }
    }
    
    // Apply all updates at once
    if (Object.keys(updates).length > 0) {
      setFormData(prev => ({ ...prev, ...updates }));
      console.log('Form populated with:', updates);
    }
    
    // Also set chapter plan review if we have chapters
    if (uploadedChaps.length > 0) {
      const chaptersForReview = uploadedChaps.map((ch, i) => ({
        number: ch.number || i + 1,
        title: ch.title || `Chapter ${ch.number || i + 1}`,
        summary: ch.summary || ch.content || '',
        key_beats: ch.key_beats || [],
        pov: ch.pov || '',
        setting: ch.setting || ''
      }));
      setPlanReviewOutline(chaptersForReview);
    }
  };

  // Generate chapter plan using AI (for 'situations' and 'ai' modes)
  const handleGenerateChapterPlan = async () => {
    setGeneratingPlan(true);
    try {
      const validProtagonists = (formData.protagonists || []).filter(p => p.name && p.name.trim());
      const buildCharacterDescription = (char) => {
        const parts = [char.description || ''];
        if (char.age) parts.push(`Age: ${char.age}`);
        if (char.gender) parts.push(`Gender: ${char.gender}`);
        if (char.personality) parts.push(`Personality: ${char.personality}`);
        if (char.backstory) parts.push(`Backstory: ${char.backstory}`);
        return parts.filter(Boolean).join('. ');
      };
      
      const charInfo = validProtagonists.map(p => `${p.name}: ${buildCharacterDescription(p)}`).join('\n');
      const situationsText = chapterPlanMode === 'situations' 
        ? userSituations.filter(s => s.description.trim()).map((s, i) => `${i + 1}. ${s.description}`).join('\n')
        : '';
      
      const payload = {
        title: formData.title,
        genre: formData.genre,
        tone: formData.tone || 'balanced',
        num_chapters: formData.num_chapters,
        characters: charInfo,
        setting: formData.setting || '',
        premise: [formData.premise_beginning, formData.premise_middle, formData.premise_ending].filter(Boolean).join(' '),
        theme: formData.theme || '',
        situations: situationsText,
        world_rules: formData.supernatural_rules || ''
      };
      
      const res = await axios.post(`${API}/generate-chapter-plan`, payload, { timeout: 120000 });
      if (res.data && res.data.chapters) {
        setPlanReviewOutline(res.data.chapters);
        toast.success("Chapter plan generated! Review and edit before creating.");
      } else {
        toast.error("Failed to generate plan. Please try again.");
      }
    } catch (error) {
      console.error("Plan generation error:", error);
      toast.error("Failed to generate chapter plan. Please try again.");
    } finally {
      setGeneratingPlan(false);
    }
  };

  const handleManualCreate = async () => {
    if (!formData.title || !formData.genre) {
      toast.error("Please fill in title and genre");
      return;
    }
    // Protagonist is optional - older projects may not have it
    setLoading(true);
    toast.info("Creating your book...", { duration: 5000 });
    try {
      // Build the payload with full character data (physical attributes + personality)
      const validProtagonists = (formData.protagonists || []).filter(p => p.name && p.name.trim());
      
      // Build rich character descriptions from physical attributes
      const buildCharacterDescription = (char) => {
        const parts = [char.description || ''];
        const physical = [];
        if (char.age) physical.push(`Age: ${char.age}`);
        if (char.gender) physical.push(`Gender: ${char.gender}`);
        if (char.height) physical.push(`Height: ${char.height}`);
        if (char.build) physical.push(`Build: ${char.build}`);
        if (char.skinTone) physical.push(`Skin: ${char.skinTone}`);
        if (char.eyeColor) physical.push(`Eyes: ${char.eyeColor}`);
        if (char.hairColor || char.hairStyle) physical.push(`Hair: ${[char.hairColor, char.hairStyle].filter(Boolean).join(', ')}`);
        if (char.distinguishingFeatures) physical.push(`Features: ${char.distinguishingFeatures}`);
        if (physical.length > 0) parts.push(`Physical: ${physical.join('. ')}`);
        if (char.personality) parts.push(`Personality: ${char.personality}`);
        if (char.motivation) parts.push(`Motivation: ${char.motivation}`);
        if (char.backstory) parts.push(`Backstory: ${char.backstory}`);
        return parts.filter(Boolean).join('. ');
      };

      const payload = {
        ...formData,
        chapter_size: chapterSize,  // "auto", "medium" or "large"
        chapter_word_target: chapterSize === "auto" ? null : (chapterSize === "medium" ? 900 : 2000),
        // Include all characters from both sources
        characters: characters.map(c => ({
          name: c.name,
          role: c.role || 'supporting',
          description: buildCharacterDescription(c),
          physical_appearance: c.physical_appearance || c.physicalAppearance || '',
          extraction_method: c.extraction_method || '',
          age: c.age || '',
          gender: c.gender || '',
          ethnicity: c.ethnicity || '',
          height: c.height || '',
          build: c.build || '',
          hair_color: c.hairColor || '',
          hair_style: c.hairStyle || '',
          eye_color: c.eyeColor || '',
          skin_tone: c.skinTone || '',
          distinguishing_features: c.distinguishingFeatures || '',
          personality: c.personality || '',
          motivation: c.motivation || '',
          backstory: c.backstory || ''
        })),
        protagonists: validProtagonists.map(p => ({
          name: p.name,
          role: p.role || 'protagonist',
          description: buildCharacterDescription(p),
          physical: {
            age: p.age || '', gender: p.gender || '', height: p.height || '',
            build: p.build || '', skinTone: p.skinTone || '', eyeColor: p.eyeColor || '',
            hairColor: p.hairColor || '', hairStyle: p.hairStyle || '',
            distinguishingFeatures: p.distinguishingFeatures || ''
          },
          personality: p.personality || '',
          motivation: p.motivation || '',
          backstory: p.backstory || ''
        })),
        protagonist_name: validProtagonists.length > 0 ? validProtagonists.map(p => p.name).join(' & ') : '',
        protagonist_description: validProtagonists.length > 0 ? validProtagonists.map(p => `${p.name}: ${buildCharacterDescription(p)}`).join('; ') : '',
        auto_generate_outline: !planReviewOutline,
        chapter_outline: planReviewOutline || undefined,
        // Include story bible if it was uploaded
        story_bible: uploadedStoryBible ? {
          ...uploadedStoryBible,
          is_canonical: true
        } : undefined
      };
      const res = await axios.post(`${API}/projects?user_id=${userId}`, payload, { timeout: 30000 });
      if (res.data && res.data.id) {
        toast.success("Book created! Opening editor...");
        onCreated(res.data);
        resetAndClose();
      } else {
        toast.error("Book created but response was invalid");
        console.error("Invalid response:", res.data);
      }
    } catch (error) {
      console.error("Project creation error:", error);
      if (error.code === 'ECONNABORTED') {
        toast.error("Request timed out. Please try again.");
      } else {
        toast.error(`Failed to create project: ${error.message || 'Unknown error'}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Journey/Blueprint: Generate outline from key situations
  const handleGenerateBlueprint = async () => {
    if (!blueprintData.title) { toast.error("Enter your book title"); return; }
    if (!blueprintData.protagonists || !blueprintData.protagonists[0]?.name) { toast.error("Enter at least one protagonist's name"); return; }
    if (!blueprintData.genre) { toast.error("Select a genre"); return; }
    if (!blueprintData.tone) { toast.error("Select the story's tone/energy"); return; }
    const validSituations = situations.filter(s => s.description && s.description.trim());
    if (validSituations.length < 2) { toast.error("Add at least 2 key situations"); return; }

    setGeneratingBlueprint(true);
    setBlueprintProgress({ part: 0, total: 3, message: 'Starting...' });
    setBlueprintOutline(null);
    playTick(); // Sound on start

    try {
      // Create clean, serializable request body with full character data
      const validProtagonists = blueprintData.protagonists.filter(p => p.name && p.name.trim());
      
      // Build rich character descriptions from physical attributes
      const buildCharacterDescription = (char) => {
        const parts = [char.description || ''];
        const physical = [];
        if (char.age) physical.push(`Age: ${char.age}`);
        if (char.gender) physical.push(`Gender: ${char.gender}`);
        if (char.height) physical.push(`Height: ${char.height}`);
        if (char.build) physical.push(`Build: ${char.build}`);
        if (char.skinTone) physical.push(`Skin: ${char.skinTone}`);
        if (char.eyeColor) physical.push(`Eyes: ${char.eyeColor}`);
        if (char.hairColor || char.hairStyle) physical.push(`Hair: ${[char.hairColor, char.hairStyle].filter(Boolean).join(', ')}`);
        if (char.distinguishingFeatures) physical.push(`Features: ${char.distinguishingFeatures}`);
        if (physical.length > 0) parts.push(`Physical: ${physical.join('. ')}`);
        if (char.personality) parts.push(`Personality: ${char.personality}`);
        if (char.motivation) parts.push(`Motivation: ${char.motivation}`);
        if (char.backstory) parts.push(`Backstory: ${char.backstory}`);
        return parts.filter(Boolean).join('. ');
      };

      const requestBody = {
        title: blueprintData.title || '',
        protagonists: validProtagonists.map(p => ({
          name: p.name || '',
          role: p.role || 'protagonist',
          description: buildCharacterDescription(p),
          physical: {
            age: p.age || '', gender: p.gender || '', height: p.height || '',
            build: p.build || '', skinTone: p.skinTone || '', eyeColor: p.eyeColor || '',
            hairColor: p.hairColor || '', hairStyle: p.hairStyle || '',
            distinguishingFeatures: p.distinguishingFeatures || ''
          },
          personality: p.personality || '',
          motivation: p.motivation || '',
          backstory: p.backstory || ''
        })),
        protagonist_name: validProtagonists.map(p => p.name).join(' & ') || '',
        protagonist_description: validProtagonists.map(p => `${p.name}: ${buildCharacterDescription(p)}`).join('; ') || '',
        antagonist_name: blueprintData.antagonist_name || '',
        antagonist_description: blueprintData.antagonist_description || '',
        genre: blueprintData.genre || '',
        tone: blueprintData.tone || '',
        num_chapters: blueprintData.num_chapters || 10,
        world_rules: blueprintData.world_rules || '',
        situations: validSituations.map((s, i) => ({
          description: s.description || '',
          time_marker: s.time_marker || 'Beginning of the story',
          order: i + 1
        })),
        // Flag for multi-protagonist story
        is_multi_protagonist: validProtagonists.length > 1
      };

      const response = await fetch(`${API}/story-blueprint/generate-outline-stream?user_id=${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        let errorMsg = 'Generation failed';
        try { const t = await response.text(); errorMsg = JSON.parse(t).detail || errorMsg; } catch(e) {}
        throw new Error(errorMsg);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let partialOutline = {};
      let receivedDone = false;
      let buffer = '';
      let lastChaptersCount = 0;

      console.log('Starting to read blueprint stream...');
      
      // Helper function to process a single SSE line
      const processLine = (line) => {
        if (line.startsWith('data: ')) {
          try {
            const jsonStr = line.slice(6).trim();
            if (!jsonStr) return;
            const data = JSON.parse(jsonStr);
            
            if (data.type === 'progress') {
              console.log('Progress:', data.part, '/', data.total, data.message);
              setBlueprintProgress({ part: data.part, total: data.total, message: data.message });
              playTick();
            } else if (data.type === 'partial') {
              if (data.data) {
                Object.keys(data.data).forEach(key => {
                  if (key === 'chapters' && Array.isArray(data.data.chapters)) {
                    if (!partialOutline.chapters) partialOutline.chapters = [];
                    partialOutline.chapters = [...partialOutline.chapters, ...data.data.chapters];
                  } else {
                    partialOutline[key] = data.data[key];
                  }
                });
              }
              if (partialOutline.chapters?.length !== lastChaptersCount) {
                lastChaptersCount = partialOutline.chapters?.length || 0;
                console.log('Chapters so far:', lastChaptersCount);
              }
              playTick();
            } else if (data.type === 'done') {
              receivedDone = true;
              console.log('DONE received! Chapters:', data.outline?.chapters?.length || 0);
              setBlueprintOutline(data.outline);
              setStep(3);
              playComplete();
              toast.success("Your story blueprint is ready!");
            } else if (data.type === 'error') {
              console.error('Error from server:', data.message);
              toast.error(data.message || "Generation encountered an issue");
            }
          } catch (e) {
            console.error('Error parsing line:', line.substring(0, 100), e.message);
          }
        }
      };

      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) {
            console.log('Stream ended. receivedDone:', receivedDone, 'partialOutline chapters:', partialOutline.chapters?.length || 0);
            // CRITICAL: Process any remaining buffer content after stream ends
            if (buffer.trim()) {
              console.log('Processing remaining buffer:', buffer.substring(0, 100));
              const remainingLines = buffer.split('\n');
              for (const line of remainingLines) {
                processLine(line);
              }
            }
            break;
          }
          
          // Append to buffer and process complete lines
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || ''; // Keep incomplete line in buffer
          
          for (const line of lines) {
            processLine(line);
          }
        }
      } catch (readError) {
        console.error('Stream read error:', readError);
      }
      
      // If stream ended without 'done' event but we have partial data, use it
      if (!receivedDone && partialOutline.chapters && partialOutline.chapters.length > 0) {
        console.log('Using partial outline with', partialOutline.chapters.length, 'chapters');
        partialOutline.title = partialOutline.title || blueprintData.title;
        partialOutline.genre = partialOutline.genre || blueprintData.genre;
        setBlueprintOutline(partialOutline);
        setStep(3);
        playComplete();
        toast.success("Outline generated!");
      } else if (!receivedDone) {
        console.error('No done event and no chapters');
        toast.error("Generation didn't complete. Please try again.");
      }
    } catch (error) {
      console.error('Blueprint generation error:', error);
      if (error?.message?.includes('subscription') || error?.message?.includes('limit')) {
        toast.error(error.message);
      } else {
        toast.error(error?.message || "Blueprint generation failed");
      }
    } finally {
      setGeneratingBlueprint(false);
    }
  };

  // Create book from blueprint outline
  const handleCreateFromBlueprint = async () => {
    if (!blueprintOutline) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/idea-to-book/create?user_id=${userId}&title=${encodeURIComponent(blueprintData.title)}&genre=${encodeURIComponent(blueprintData.genre)}&tone=${encodeURIComponent(blueprintData.tone)}&logline=${encodeURIComponent(blueprintOutline.logline || '')}&setting=${encodeURIComponent(blueprintOutline.setting || '')}&ending_type=${encodeURIComponent(blueprintOutline.ending_type || 'triumphant')}`, {
        characters: blueprintOutline.characters || [],
        plot_points: blueprintOutline.plot_points || [],
        chapters: blueprintOutline.chapters || [],
        themes: blueprintOutline.themes || []
      });
      // Clear the specific draft on successful creation
      try {
        const drafts = JSON.parse(localStorage.getItem(getDraftKey('journey')) || '{}');
        const draftKey = blueprintData.title.toLowerCase().trim();
        if (drafts[draftKey]) {
          delete drafts[draftKey];
          localStorage.setItem(getDraftKey('journey'), JSON.stringify(drafts));
        }
      } catch (e) {}
      toast.success("Book created from your blueprint! Time to write!");
      onCreated(res.data);
      resetAndClose();
    } catch (error) {
      toast.error("Failed to create book");
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateOutline = async () => {
    if (!ideaData.idea || ideaData.idea.length < 10) {
      toast.error("Please describe your book idea (at least a sentence)");
      return;
    }
    if (!ideaData.genre) {
      toast.error("Please select a genre for your book");
      return;
    }
    setGeneratingOutline(true);
    setOutlineProgress({ part: 0, total: 4, message: 'Starting...' });
    playTick();
    
    try {
      addLog('progress', 'Building story outline...', 'Please wait');
      
      const requestData = {
        idea: String(ideaData.idea || ''),
        title: String(ideaData.title || ''),
        genre: String(ideaData.genre || ''),
        num_chapters: Number(ideaData.num_chapters) || 10,
        audience_age: String(ideaData.audience_age || 'adult'),
        content_rating: String(ideaData.content_rating || 'moderate'),
        tone: String(ideaData.tone || 'balanced'),
        location: String(ideaData.location || ''),
        time_period: String(ideaData.time_period || ''),
        must_have_points: String(ideaData.must_have_points || '')
      };
      
      // Create the outline job
      const jobResponse = await axios.post(`${API}/outline-jobs?user_id=${userId}`, requestData);
      const jobId = jobResponse.data.job_id;
      addLog('progress', 'Outline job created', `Job: ${jobId.slice(0, 8)}...`);
      
      // Poll for status
      const pollInterval = setInterval(async () => {
        try {
          const statusRes = await axios.get(`${API}/outline-jobs/${jobId}/status`);
          const job = statusRes.data;
          
          // Update progress
          setOutlineProgress({ 
            part: job.current_step, 
            total: job.total_steps, 
            message: ['Starting...', 'Creating story foundation...', 'Creating characters...', 'Structuring plot...', 'Completing chapters...'][job.current_step] || 'Working...'
          });
          
          if (job.current_step > 0) {
            addLog('progress', `Step ${job.current_step} of ${job.total_steps}`, ['', 'Story foundation', 'Characters', 'Plot structure', 'Chapters'][job.current_step] || '');
            playTick();
          }
          
          if (job.status === 'COMPLETED') {
            clearInterval(pollInterval);
            setOutline(job.outline);
            setStep(2);
            playComplete();
            addLog('success', 'Outline complete!', job.outline?.title || 'Ready');
            toast.success("Outline generated! Review and customize below.");
            setGeneratingOutline(false);
            setOutlineProgress({ part: 0, total: 4, message: '' });
          } else if (job.status === 'FAILED') {
            clearInterval(pollInterval);
            addLog('error', 'OUTLINE FAILED', job.error_message || 'Unknown error');
            toast.error(job.error_message || "Failed to generate outline. Please try again.");
            setGeneratingOutline(false);
            setOutlineProgress({ part: 0, total: 4, message: '' });
          }
        } catch (pollError) {
          console.error('Poll error:', pollError);
          // Don't stop polling on temporary errors
        }
      }, 2000); // Poll every 2 seconds
      
      // Safety timeout after 5 minutes
      setTimeout(() => {
        clearInterval(pollInterval);
        if (generatingOutline) {
          setGeneratingOutline(false);
          addLog('error', 'Timeout', 'Outline generation took too long');
          toast.error("Generation timed out. Please try again with fewer chapters.");
        }
      }, 300000);
      
    } catch (error) {
      console.error("Outline generation error:", error);
      const errorMessage = error?.response?.data?.detail || error?.message || 'Unknown error';
      addLog('error', 'OUTLINE ERROR', errorMessage);
      toast.error(errorMessage || "Failed to start outline generation.");
      setGeneratingOutline(false);
      setOutlineProgress({ part: 0, total: 4, message: '' });
    }
  };

  const handleCreateFromOutline = async () => {
    if (!outline) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        user_id: userId,
        title: outline.title,
        genre: outline.genre,
        setting: outline.setting || "",
        audience_age: ideaData.audience_age,
        content_rating: ideaData.content_rating,
        tone: ideaData.tone,
        logline: outline.logline || "",
        ending_type: outline.ending_type || "triumphant"
      });
      
      const res = await axios.post(`${API}/idea-to-book/create?${params.toString()}`, {
        characters: outline.characters || [],
        plot_points: outline.plot_points || [],
        chapters: outline.chapters || [],
        themes: outline.themes || []
      });
      
      toast.success("Book created from your idea! Time to write!");
      if (onCreated) {
        onCreated(res.data);
      }
      resetAndClose();
      if (!onCreated) {
        navigate(`/write/${res.data.id}`);
      }
    } catch (error) {
      console.error("Create from outline error:", error);
      toast.error("Failed to create book: " + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Handle Quick Story Generation
  // skipDnaGate=true (explicit) bypasses the DNA-confirm gate — used by the DNA
  // panel's Confirm button, whose state update hasn't propagated into this closure yet.
  const handleGenerateQuickStory = async (skipDnaGate = false) => {
    if (!quickStoryData.idea || quickStoryData.idea.length < 20) {
      toast.error("Please provide a story idea (at least 20 characters)");
      return;
    }

    // ===== STORY DNA PANEL: extract canon first, let user verify + lock =====
    if (!quickStoryDnaConfirmed && skipDnaGate !== true) {
      setGeneratingQuickStory(true);
      setQuickStoryProgress({ step: 0, total: 4, message: 'Reading your story DNA...' });
      addLog('progress', 'Extracting Story DNA...', 'Identifying characters, events, and required ending');
      try {
        const dnaRes = await axios.post(`${API}/canon/extract`, {
          idea: quickStoryData.idea,
          genre: quickStoryData.genre,
          is_voice: false,
          files: [],
        }, { timeout: 90000 });
        setQuickStoryCanon(dnaRes.data.canon);
        // Pre-select all suggested locks (user can toggle off)
        setQuickStoryLocks(new Set(dnaRes.data.suggested_locks || []));
        addLog('success', 'Story DNA ready', `${dnaRes.data.summary.characters_found} characters, ${dnaRes.data.summary.events_found} events`);
        setGeneratingQuickStory(false);
        return; // Show the DNA panel, wait for user confirm
      } catch (err) {
        console.error('Canon extract failed:', err);
        addLog('error', 'Story DNA failed — proceeding without preview', err.message);
        // Fall through to generate without preview
        setQuickStoryDnaConfirmed(true);
      }
    }

    setGeneratingQuickStory(true);
    setQuickStoryProgress({ step: 1, total: 4, message: 'Creating your story...' });
    addLog('progress', 'Starting faithful story rendering...', `Target: ${quickStoryData.word_count} words`);

    try {
      // Create the quick story job with timeout
      const jobResponse = await axios.post(`${API}/quick-story-jobs?user_id=${userId}`, {
        idea: quickStoryData.idea,
        title: quickStoryData.title || '',
        genre: quickStoryData.genre,
        word_count: quickStoryData.word_count,
        audience_age: quickStoryData.audience_age,
        content_rating: quickStoryData.content_rating,
        tone: quickStoryData.tone,
        canon: quickStoryCanon,                    // pass pre-extracted canon (skips Stage 1)
        locked_items: Array.from(quickStoryLocks), // user-selected 🔒 locks
      }, { timeout: 30000 }); // 30 second timeout for job creation

      const jobId = jobResponse.data.job_id;
      addLog('progress', 'Story job created', `Job: ${jobId.slice(0, 8)}...`);

      // Poll for status with error handling
      let attempts = 0;
      const maxAttempts = 60; // ~3 minutes max (60 * 3s)
      let consecutiveErrors = 0;

      while (attempts < maxAttempts) {
        attempts++;
        await new Promise(r => setTimeout(r, 3000));

        try {
          const statusRes = await axios.get(`${API}/quick-story-jobs/${jobId}/status`, { timeout: 60000 });
          const job = statusRes.data;
          consecutiveErrors = 0; // Reset error counter on success

          setQuickStoryProgress({
            step: job.current_step,
            total: job.total_steps,
            message: job.current_step === 1 ? 'Writing your story...' : 'Finalizing...'
          });

          if (job.status === 'COMPLETED') {
            addLog('success', 'Story complete!', `"${job.title}" - ${job.word_count} words`);
            toast.success(`Your short story "${job.title}" is ready! (${job.word_count} words)`);
            resetAndClose();
            navigate(`/write/${job.project_id}`);
            return;
          } else if (job.status === 'FAILED') {
            addLog('error', 'Story generation failed', job.error_message || 'Unknown error');
            toast.error(job.error_message || "Failed to generate story. Please try again.");
            setGeneratingQuickStory(false);
            return;
          }
        } catch (pollError) {
          consecutiveErrors++;
          console.warn(`Poll attempt ${attempts} failed:`, pollError.message);
          addLog('progress', `Checking status... (attempt ${attempts})`, 'Still working');
          
          // Allow up to 3 consecutive network errors before giving up
          if (consecutiveErrors >= 3) {
            throw new Error('Lost connection to server. Please check your internet and try again.');
          }
          // Continue polling - the job might still be running
        }
      }

      // Timeout
      addLog('error', 'Timeout', 'Story generation took too long');
      toast.error("Generation timed out. Please try again.");
      setGeneratingQuickStory(false);

    } catch (error) {
      console.error("Quick story error:", error);
      const errorMessage = error?.response?.data?.detail || error?.message || 'Unknown error';
      addLog('error', 'Generation error', errorMessage);
      toast.error(errorMessage);
      setGeneratingQuickStory(false);
    }
  };

  const handleVoiceTransform = async () => {
    if (!voiceTranscript || voiceTranscript.length < 20) {
      toast.error("Please speak more about your story idea");
      return;
    }
    setProcessingVoice(true);
    addLog('progress', 'Transforming your voice into a story...');
    try {
      // SHORT stories use async job pattern (avoids Cloudflare 100s timeout on long LLM calls)
      if (voiceTargetLength === "short") {
        const jobRes = await axios.post(`${API}/voice-to-story-jobs`, {
          transcript: voiceTranscript,
          user_id: userId,
          genre: voiceGenre,
          target_length: "short",
        }, { timeout: 30000 });
        const jobId = jobRes.data.job_id;
        addLog('progress', 'Story job created', `Job: ${jobId.slice(0, 8)}...`);
        
        // Poll for completion (~3 minutes max)
        const maxAttempts = 60; // 60 * 3s = 180s
        for (let attempt = 0; attempt < maxAttempts; attempt++) {
          await new Promise(r => setTimeout(r, 3000));
          let statusRes;
          try {
            statusRes = await axios.get(`${API}/voice-to-story-jobs/${jobId}/status`, { timeout: 30000 });
          } catch (pollErr) {
            // Transient poll error — try again
            continue;
          }
          const job = statusRes.data;
          if (job.status === 'COMPLETED') {
            addLog('success', `Created "${job.title}"!`, `${job.word_count} words`);
            toast.success(`Created "${job.title}"! ${job.word_count} words.`);
            resetAndClose();
            navigate(`/write/${job.project_id}`);
            return;
          }
          if (job.status === 'FAILED') {
            addLog('error', 'Story generation failed', job.error_message || 'Unknown error');
            toast.error(job.error_message || "Failed to generate story. Please try again.");
            return;
          }
          // PENDING / PROCESSING — keep polling
        }
        addLog('error', 'Voice story timed out');
        toast.error("Generation took too long. Please try again.");
        return;
      }
      
      // OUTLINE (medium/long) — synchronous (fast enough)
      const res = await axios.post(`${API}/voice-to-story`, {
        transcript: voiceTranscript,
        user_id: userId,
        genre: voiceGenre,
        target_length: voiceTargetLength
      }, { timeout: 120000 });
      if (res.data.success) {
        const result = res.data.result;
        addLog('success', `Created "${result.title}"!`);
        setOutline(result);
        setIdeaData(prev => ({ ...prev, title: result.title, genre: result.genre, idea: voiceTranscript }));
        setMode("idea");
        setStep(2);
        toast.success(`Outline created for "${result.title}"!`);
      } else {
        addLog('error', 'Voice processing failed');
        toast.error("Failed to process voice - please try again");
      }
    } catch (error) {
      console.error('Voice to story error:', error);
      addLog('error', error?.response?.data?.detail || error.message || "Unknown error");
      if (error.code === 'ECONNABORTED') {
        toast.error("Request timed out. Your story idea may be too long - try a shorter description.");
      } else {
        toast.error(error?.response?.data?.detail || "Failed to process voice - please try again");
      }
    } finally {
      setProcessingVoice(false);
    }
  };


  const resetAndClose = () => {
    setMode("choose");
    setStep(1);
    setOutline(null);
    setBlueprintOutline(null);
    setGeneratingBlueprint(false);
    setShowDraftPicker(false);
    setAvailableDrafts([]);
    setComplexityLevel(null); // Reset complexity level
    setSmartUploadMode(false); // Reset smart upload mode
    setUploadedStoryBible(null); // Reset uploaded story bible
    // Story DNA state
    setQuickStoryCanon(null);
    setQuickStoryLocks(new Set());
    setQuickStoryDnaConfirmed(false);
    setSituations([{ description: "", time_marker: "Beginning of the story" }]);
    setBlueprintData({
      title: "", protagonists: [{ name: "", description: "" }],
      antagonist_name: "", antagonist_description: "", genre: "", tone: "", num_chapters: 10, world_rules: ""
    });
    setFormData({
      title: "", genre: "", book_type: "fiction", target_length: "novel", audience_age: "adult",
      content_rating: "moderate", specialty_subject: "general", tone: "balanced",
      protagonists: [{ name: "", description: "" }], setting: "", initial_premise: "",
      is_series: false, series_name: "", total_books: 1, book_number: 1, author_credentials: "",
      num_chapters: 10, pov: "third_limited", tense: "past",
      world_type: "earth", world_name: "", era: "contemporary", era_custom: "",
      tech_level: "modern", tech_importance: "background",
      has_supernatural: false, supernatural_type: [], supernatural_rules: "", supernatural_source: "",
      has_religion: false, religion_type: "mono", religion_importance: "background", religion_description: "",
      society_structure: "democratic", social_classes: false,
      antagonist_name: "", antagonist_description: "", antagonist_type: "human",
      story_arc: "hero_journey", central_conflict: "", theme: ""
    });
    setIdeaData({
      title: "", idea: "", genre: "", num_chapters: 10, audience_age: "adult", content_rating: "moderate", tone: "balanced",
      location: "", time_period: "", must_have_points: ""
    });
    // Reset quick story state
    setQuickStoryData({
      idea: "", title: "", genre: "literary", word_count: 1500,
      audience_age: "adult", content_rating: "moderate", tone: "balanced"
    });
    setGeneratingQuickStory(false);
    setQuickStoryProgress({ step: 0, total: 2, message: '' });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen && mode === "choose") resetAndClose(); else if (!isOpen) { /* prevent accidental close during wizard */ } }}>
      <DialogContent 
        className={mode === "choose" ? "sm:max-w-[600px] max-h-[90vh] overflow-y-auto" : mode === "upload" ? "sm:max-w-[720px] w-[95vw] max-h-[90vh] overflow-y-auto" : mode === "genre_template" ? "sm:max-w-[650px] max-h-[90vh] overflow-y-auto" : "sm:max-w-[600px] max-h-[90vh] overflow-y-auto"}
        onInteractOutside={(e) => { if (mode !== "choose") e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (mode !== "choose") e.preventDefault(); }}
      >
        <DialogHeader className="pb-2">
          <DialogTitle className="font-literary flex items-center gap-2 text-base sm:text-lg">
            <ShweebumCat size="sm" />
            {mode === "choose" && "Start Your Story"}
            {mode === "builder" && "Story Builder"}
            {mode === "manual" && "Create Your Book"}
            {mode === "idea" && (step === 1 ? "Turn Your Idea Into a Book" : "Review Your Book Outline")}
            {mode === "journey" && (step === 1 ? "Your Story's Key Moments" : step === 2 ? "Map Your Situations" : "Review Your Blueprint")}
            {mode === "quick" && "Quick Story Generator"}
            {mode === "voice" && "Tell Me Your Story"}
            {mode === "genre_template" && "Genre Blueprints"}
          </DialogTitle>
          
          {/* Auto-save indicator */}
          {mode !== "choose" && lastAutoSave && (
            <div className="absolute left-4 top-4 flex items-center gap-1 text-xs text-green-600">
              <Check className="w-3 h-3" />
              <span>Auto-saved {lastAutoSave.toLocaleTimeString()}</span>
            </div>
          )}
          
          {mode !== "choose" && (
            <button 
              onClick={resetAndClose} 
              className="absolute right-4 top-4 text-xs text-muted-foreground hover:text-muted-foreground underline"
              data-testid="wizard-close-btn"
            >
              Cancel
            </button>
          )}
          
          {/* Progress Indicator for Idea Mode */}
          {mode === "idea" && (
            <div className="flex items-center justify-center gap-1 mt-3 text-xs">
              <span className={`px-2 py-1 rounded ${step >= 1 ? 'bg-cyan-600 text-white' : 'bg-slate-700 text-muted-foreground'}`}>Idea</span>
              <span className="text-slate-300">→</span>
              <span className={`px-2 py-1 rounded ${step >= 2 ? 'bg-cyan-600 text-white' : 'bg-slate-700 text-muted-foreground'}`}>Outline</span>
              <span className="text-slate-300">→</span>
              <span className="px-2 py-1 rounded bg-slate-700 text-muted-foreground">Plan</span>
              <span className="text-slate-300">→</span>
              <span className="px-2 py-1 rounded bg-slate-700 text-muted-foreground">Write</span>
              <span className="text-slate-300">→</span>
              <span className="px-2 py-1 rounded bg-slate-700 text-muted-foreground">Finish</span>
            </div>
          )}

          {/* Progress Indicator for Journey Mode */}
          {mode === "journey" && (
            <div className="flex items-center justify-center gap-1 mt-3 text-xs">
              <span className={`px-2 py-1 rounded ${step >= 1 ? 'bg-indigo-500 text-white' : 'bg-slate-700 text-muted-foreground'}`}>Basics</span>
              <span className="text-slate-300">→</span>
              <span className={`px-2 py-1 rounded ${step >= 2 ? 'bg-indigo-500 text-white' : 'bg-slate-700 text-muted-foreground'}`}>Situations</span>
              <span className="text-slate-300">→</span>
              <span className={`px-2 py-1 rounded ${step >= 3 ? 'bg-indigo-500 text-white' : 'bg-slate-700 text-muted-foreground'}`}>Outline</span>
            </div>
          )}
        </DialogHeader>
        
        {/* PROMINENT SAVE DRAFT BANNER - Only shows on Journey step 2 */}
        {mode === "journey" && step === 2 && (
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-3 mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Save className="w-5 h-5 text-indigo-600" />
              <span className="text-sm text-indigo-800">
                <strong>{situations.filter(s => s.description.trim()).length}</strong> situations added
              </span>
            </div>
            <Button
              onClick={() => {
                try {
                  if (!blueprintData.title) {
                    toast.error("Please add a title on the Basics step first");
                    return;
                  }
                  // Create clean, serializable copies of the data with multi-protagonist support
                  const cleanBlueprintData = {
                    title: blueprintData.title || '',
                    protagonists: (blueprintData.protagonists || []).map(p => ({
                      name: p.name || '',
                      description: p.description || ''
                    })),
                    antagonist_name: blueprintData.antagonist_name || '',
                    antagonist_description: blueprintData.antagonist_description || '',
                    genre: blueprintData.genre || '',
                    tone: blueprintData.tone || '',
                    num_chapters: blueprintData.num_chapters || 10,
                    world_rules: blueprintData.world_rules || ''
                  };
                  const cleanSituations = situations.map(s => ({
                    description: s.description || '',
                    time_marker: s.time_marker || 'Beginning of the story'
                  }));
                  
                  const drafts = JSON.parse(localStorage.getItem(getDraftKey('journey')) || '{}');
                  const draftKey = blueprintData.title.toLowerCase().trim();
                  drafts[draftKey] = { 
                    blueprintData: cleanBlueprintData, 
                    situations: cleanSituations, 
                    savedAt: new Date().toISOString(),
                    title: blueprintData.title 
                  };
                  localStorage.setItem(getDraftKey('journey'), JSON.stringify(drafts));
                  toast.success(`Draft saved! "${blueprintData.title}" - You can safely close and come back anytime.`);
                } catch (err) {
                  console.error('Save draft error:', err);
                  toast.error("Failed to save draft. Please try again.");
                }
              }}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              size="sm"
              data-testid="save-draft-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              Save Draft
            </Button>
          </div>
        )}

        {/* Mode Selection */}
        {(mode === "choose" || showDraftPicker) && (
          <ModeSelector
            setMode={setMode}
            showDraftPicker={showDraftPicker}
            setShowDraftPicker={setShowDraftPicker}
            availableDrafts={availableDrafts}
            setAvailableDrafts={setAvailableDrafts}
            setBlueprintData={setBlueprintData}
            setSituations={setSituations}
            setStep={setStep}
            setHasUnsavedChanges={setHasUnsavedChanges}
            freebumMode={freebumMode}
          />
        )}

        {/* STORY BUILDER MODE - Guided Wizard */}
        {mode === "builder" && (
          <StoryBuilderWizard
            userId={userId}
            genres={genres}
            onCreated={(project) => {
              onCreated(project);
              resetAndClose();
            }}
            onCancel={resetAndClose}
          />
        )}

        {/* UPLOAD MODE */}
        {mode === "upload" && (
          <UploadMode
            uploadStep={uploadStep} setUploadStep={setUploadStep}
            uploadedStoryBible={uploadedStoryBible} setUploadedStoryBible={setUploadedStoryBible}
            uploadedChaptersVerbatim={uploadedChaptersVerbatim} setUploadedChaptersVerbatim={setUploadedChaptersVerbatim}
            uploadedCharacterProfiles={uploadedCharacterProfiles} setUploadedCharacterProfiles={setUploadedCharacterProfiles}
            uploadedWorldRules={uploadedWorldRules} setUploadedWorldRules={setUploadedWorldRules}
            uploadedCategories={uploadedCategories} setUploadedCategories={setUploadedCategories}
            uploadConflicts={uploadConflicts} setUploadConflicts={setUploadConflicts}
            conflictResolutions={conflictResolutions} setConflictResolutions={setConflictResolutions}
            resolvingConflicts={resolvingConflicts} setResolvingConflicts={setResolvingConflicts}
            analyzingFile={analyzingFile} setAnalyzingFile={setAnalyzingFile}
            manuscriptId={manuscriptId}
            manuscriptAnalysis={manuscriptAnalysis} setManuscriptAnalysis={setManuscriptAnalysis}
            manuscriptDiagnosis={manuscriptDiagnosis} setManuscriptDiagnosis={setManuscriptDiagnosis}
            manuscriptOutlines={manuscriptOutlines} setManuscriptOutlines={setManuscriptOutlines}
            uploadActivityLog={uploadActivityLog} setUploadActivityLog={setUploadActivityLog}
            selectedImprovements={selectedImprovements} setSelectedImprovements={setSelectedImprovements}
            fixingIssue={fixingIssue} setFixingIssue={setFixingIssue}
            fixedIssues={fixedIssues} setFixedIssues={setFixedIssues}
            appliedIdeas={appliedIdeas} setAppliedIdeas={setAppliedIdeas}
            applyingIdea={applyingIdea} setApplyingIdea={setApplyingIdea}
            previewingIdea={previewingIdea} setPreviewingIdea={setPreviewingIdea}
            previewChanges={previewChanges} setPreviewChanges={setPreviewChanges}
            uploadingStoryBible={uploadingStoryBible} setUploadingStoryBible={setUploadingStoryBible}
            uploadingOutline={uploadingOutline} setUploadingOutline={setUploadingOutline}
            formData={formData} setFormData={setFormData}
            planReviewOutline={planReviewOutline} setPlanReviewOutline={setPlanReviewOutline}
            chapterPlanMode={chapterPlanMode} setChapterPlanMode={setChapterPlanMode}
            chapterSize={chapterSize} setChapterSize={setChapterSize}
            complexityLevel={complexityLevel} setComplexityLevel={setComplexityLevel}
            setMode={setMode} setStep={setStep} setLoading={setLoading}
            loading={loading}
            onCreated={onCreated}
            onOpenChange={onOpenChange}
            API={API} userId={userId} navigate={navigate}
            pollManuscriptOutlines={pollManuscriptOutlines} playComplete={playComplete}
            populateFormFromUploads={populateFormFromUploads}
          />
        )}

        {/* MANUAL MODE */}
        {mode === "manual" && (
          <ManualBuilderMode
            formData={formData} setFormData={setFormData}
            step={step} setStep={setStep}
            complexityLevel={complexityLevel} setComplexityLevel={setComplexityLevel}
            characters={characters} setCharacters={setCharacters}
            chapterPlanMode={chapterPlanMode} setChapterPlanMode={setChapterPlanMode}
            chapterSize={chapterSize} setChapterSize={setChapterSize}
            planReviewOutline={planReviewOutline} setPlanReviewOutline={setPlanReviewOutline}
            generatingPlan={generatingPlan}
            userSituations={userSituations} setUserSituations={setUserSituations}
            manualChapters={manualChapters} setManualChapters={setManualChapters}
            uploadedStoryBible={uploadedStoryBible} setUploadedStoryBible={setUploadedStoryBible}
            uploadedChaptersVerbatim={uploadedChaptersVerbatim}
            smartUploadMode={smartUploadMode} setSmartUploadMode={setSmartUploadMode}
            uploadingStoryBible={uploadingStoryBible} setUploadingStoryBible={setUploadingStoryBible}
            uploadingOutline={uploadingOutline} setUploadingOutline={setUploadingOutline}
            uploadConflicts={uploadConflicts} setUploadConflicts={setUploadConflicts}
            conflictResolutions={conflictResolutions} setConflictResolutions={setConflictResolutions}
            resolvingConflicts={resolvingConflicts} setResolvingConflicts={setResolvingConflicts}
            uploadActivityLog={uploadActivityLog} setUploadActivityLog={setUploadActivityLog}
            hasUnsavedChanges={hasUnsavedChanges} setHasUnsavedChanges={setHasUnsavedChanges}
            loading={loading}
            genres={genres}
            setMode={setMode}
            onGenerateChapterPlan={handleGenerateChapterPlan}
            onManualCreate={handleManualCreate}
            onPopulateFromUploads={populateFormFromUploads}
            extractAge={extractAge} extractGender={extractGender} extractField={extractField}
            API={API}
            bookOptions={bookOptions}
          />
        )}

        {/* JOURNEY/BLUEPRINT MODE */}
        {mode === "journey" && (
          <JourneyMode
            step={step} setStep={setStep}
            blueprintData={blueprintData} setBlueprintData={setBlueprintData}
            situations={situations} setSituations={setSituations}
            generatingBlueprint={generatingBlueprint}
            blueprintProgress={blueprintProgress}
            blueprintOutline={blueprintOutline} setBlueprintOutline={setBlueprintOutline}
            loading={loading}
            hasUnsavedChanges={hasUnsavedChanges} setHasUnsavedChanges={setHasUnsavedChanges}
            onGenerateBlueprint={handleGenerateBlueprint}
            onCreateFromBlueprint={handleCreateFromBlueprint}
            onBack={() => setMode("choose")}
          />
        )}

        {/* IDEA TO BOOK MODE */}
        {mode === "idea" && (
          <IdeaMode
            step={step} setStep={setStep}
            ideaData={ideaData} setIdeaData={setIdeaData}
            outline={outline} setOutline={setOutline}
            genres={genres}
            generatingOutline={generatingOutline}
            outlineProgress={outlineProgress}
            loading={loading}
            onGenerateOutline={handleGenerateOutline}
            onCreateFromOutline={handleCreateFromOutline}
            onBack={() => setMode("choose")}
          />
        )}

        {/* Quick Story Mode */}
        {mode === "quick" && (
          <>
            {/* Story DNA Panel (P0 #1 + #2) — shown after canon extraction, before generation */}
            {quickStoryCanon && !quickStoryDnaConfirmed && (
              <StoryDnaPanel
                canon={quickStoryCanon}
                locks={quickStoryLocks}
                setLocks={setQuickStoryLocks}
                onConfirm={() => {
                  setQuickStoryDnaConfirmed(true);
                  // Pass explicit skipDnaGate — the state update above is async and
                  // this closure still sees the old value (fixed sign-off-then-stall bug)
                  handleGenerateQuickStory(true);
                }}
                onCancel={() => {
                  setQuickStoryCanon(null);
                  setQuickStoryLocks(new Set());
                  setQuickStoryDnaConfirmed(false);
                  setGeneratingQuickStory(false);
                }}
              />
            )}
            {(!quickStoryCanon || quickStoryDnaConfirmed) && (
              <QuickStoryMode
                quickStoryData={quickStoryData} setQuickStoryData={setQuickStoryData}
                generatingQuickStory={generatingQuickStory}
                quickStoryProgress={quickStoryProgress}
                onGenerateQuickStory={handleGenerateQuickStory}
                onBack={() => setMode("choose")}
              />
            )}
          </>
        )}

        {/* Voice to Story Mode */}
        {mode === "voice" && (
          <VoiceMode
            voiceTranscript={voiceTranscript} setVoiceTranscript={setVoiceTranscript}
            isRecordingVoice={isRecordingVoice} setIsRecordingVoice={setIsRecordingVoice}
            voiceGenre={voiceGenre} setVoiceGenre={setVoiceGenre}
            voiceTargetLength={voiceTargetLength} setVoiceTargetLength={setVoiceTargetLength}
            processingVoice={processingVoice}
            onTransformVoice={handleVoiceTransform}
            onBack={() => { setMode("choose"); setVoiceTranscript(""); }}
          />
        )}

        {/* Genre Blueprint Mode */}
        {mode === "genre_template" && (
          <GenreTemplateMode
            onBack={() => setMode("choose")}
            userId={userId}
            onCreated={(project) => {
              onCreated(project);
              resetAndClose();
            }}
          />
        )}

        {/* Activity Box for all modes */}
        {mode !== "choose" && !showDraftPicker && (
          <div className="mt-4 border-t border-border pt-4">
            <ActivityBox
              logs={activityLogs}
              onAddLog={addLog}
              onClearLogs={clearLogs}
              sectionName={mode === "idea" ? "Build My Story" : mode === "journey" ? "Story Journey" : mode === "quick" ? "Quick Story" : mode === "voice" ? "Voice Story" : "Story Setup"}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};



export default NewProjectDialog;
